import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, switchMap, take, tap } from 'rxjs/operators';
import {
  CHECK_TOKEN_SESSION_URL,
  CREATE_SESSION_URL,
  LOCALSTORAGE_REFRESH_TOKEN,
  LOCALSTORAGE_TOKEN,
  PATH_LOGIN,
  REFRESH_TOKEN_SESSION_URL,
  UNPROTECTED_ROUTES,
} from '../utils/cst';
import { ReLoginModalComponent } from '../views/authentification/re-login-modal/re-login-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';

@Injectable({ providedIn: 'root' })
export class InterceptorService implements HttpInterceptor {
  // Used to return the expected response from a RXJS observable
  private static readonly DEFAULT_200_RESPONSE = new HttpResponse({
    status: 200,
    body: null,
  });

  private isRefreshing: boolean = false;

  constructor(
    private spinner: NgxSpinnerService,
    private router: Router,
    private toaster: ToastrService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private keycloak: KeycloakService,
    private httpClient: HttpClient
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token = localStorage.getItem(LOCALSTORAGE_TOKEN);
    let request = this.buildRequestWithAuth(req, token);

    return next.handle(request).pipe(
      tap((response) => {
        if (response instanceof HttpResponse) {
          this.updateTokens(response); // Updates local storage and keycloak instance after refresh
        }
      }),
      catchError((err) => {
        if (
          (err.status === 401 || err.status === 403) &&
          request.url !== CREATE_SESSION_URL &&
          request.url !== CHECK_TOKEN_SESSION_URL
        ) {
          return this.changeTokenAndRetry(req, next);
        }
        if (err.status === 0 || err.status === 503) {
          localStorage.clear();
          this.spinner.hide();
          this.toaster.error(
            this.translate.instant('services.backend-unreachable')
          );
          this.router.navigate([PATH_LOGIN]);
          return EMPTY;
        }
        return throwError(() => err);
      })
    );
  }

  // ****************************************************************************
  // Private methods
  // ****************************************************************************

  private buildRequestWithAuth(
    req: HttpRequest<any>,
    token: string | null
  ): HttpRequest<any> {
    if (!token || UNPROTECTED_ROUTES.includes(req.url)) {
      return req.clone({ headers: new HttpHeaders(), withCredentials: true }); // Empty headers
    }
    // Upload, import, convert and template do not have a JSON body
    const headers =
      req.url.includes('/upload') ||
      req.url.includes('/import') ||
      req.url.includes('/convert') ||
      req.url.includes('/template')
        ? new HttpHeaders({
            Authorization: 'Bearer ' + token,
          })
        : new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          });
    return req.clone({ headers, withCredentials: true });
  }

  private updateTokens(response: HttpResponse<any>) {
    if (response.headers.has(LOCALSTORAGE_TOKEN)) {
      const token = response.headers.get(LOCALSTORAGE_TOKEN);
      localStorage.setItem(LOCALSTORAGE_TOKEN, token);
      this.keycloak.getKeycloakInstance().token = token;
    }
    if (response.headers.has(LOCALSTORAGE_REFRESH_TOKEN)) {
      const refreshToken = response.headers.get(LOCALSTORAGE_REFRESH_TOKEN);
      localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, refreshToken);
      this.keycloak.getKeycloakInstance().refreshToken = refreshToken;
    }
  }

  private changeTokenAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshing && request.url !== REFRESH_TOKEN_SESSION_URL) {
      return EMPTY;
    }
    this.isRefreshing = true;
    const refreshToken = localStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN);
    // Open the modal if the refresh token is absent / invalid
    if (!refreshToken || request.url === REFRESH_TOKEN_SESSION_URL) {
      return this.openReloginModal(request, next);
    }

    return this.httpClient.post(REFRESH_TOKEN_SESSION_URL, refreshToken).pipe(
      switchMap(() => {
        // replay the request that failed
        const newToken = localStorage.getItem(LOCALSTORAGE_TOKEN);
        const newRequest = this.buildRequestWithAuth(request, newToken);
        return next.handle(newRequest);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  private openReloginModal(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Close the spinner and show the re-login modal
    this.spinner.hide();
    this.toaster.error(this.translate.instant('services.session-expired'));

    const dialogRef = this.dialog.open(ReLoginModalComponent, {
      disableClose: true,
    });
    return dialogRef.afterClosed().pipe(
      take(1),
      switchMap((success: boolean) => {
        if (success) {
          const newToken = localStorage.getItem(LOCALSTORAGE_TOKEN);
          if (newToken && request.url !== REFRESH_TOKEN_SESSION_URL) {
            const newRequest = this.buildRequestWithAuth(request, newToken);
            return next.handle(newRequest); // Replay the request that got 401 after re-login
          }
          // Since the last request is refresh-token, we don't need to retry it
          // So we return a dummy success response because we are logged in
          return of(InterceptorService.DEFAULT_200_RESPONSE);
        }
        return EMPTY; // User did not log in again
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }
}
